import {
  SmartConnectorContextType,
  SmartConnectorErrors,
  SmartConnectorPatchResult,
  defaultContext,
  defaultSmartConnector,
} from '../SmartConnectorWizard/context';
import { useDispatch } from 'react-redux';
import { useAsync, useAsyncFn } from 'react-use';
import { useHistory, useParams } from 'react-router-dom';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  SmartConnector,
  SmartConnectorBase,
  SmartConnectorDTO,
  SmartConnectorMetaData,
  SmartConnectorStep,
} from 'pages/SmartConnectors/types';
import {
  PROPS_THAT_NEED_DEACTIVATION_ON_CHANGE,
  SMARTCONNECTORS_PATHNAME,
  SMART_CONNECTOR_STATUSES,
  SMART_CONNECTOR_STEPS,
  STEP_DATA_BY_STEP,
} from 'pages/SmartConnectors/constants';
import SmartConnectorService from 'services/SmartConnectorService';
import { setError } from 'store/errors';
import { AxiosError } from 'axios';
import { getSmartConnectorDTO, getStepData } from '../helpers';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { isEqual } from 'lodash';
import { useSmartConnectorTitle } from './useSmartConnectorTitle';
import { useDeactivationModal } from './useDeactivationModal';
import { getIsSmartConnectorActive } from 'pages/SmartConnectors/helpers';
import { flushSync } from 'react-dom';

const initialResult: SmartConnectorPatchResult = [null, null];

export const useSmartConnectorContext = (): SmartConnectorContextType => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory<{
    step?: SmartConnectorStep;
  }>();
  const dispatch = useDispatch();

  // state //////////////////////////////////////////////////////////

  const [smartConnector, setSmartConnector] = useState<
    SmartConnectorBase | SmartConnector
  >(defaultSmartConnector);

  const [step, setStep] = useState<SmartConnectorStep>(
    history.location.state?.step || SMART_CONNECTOR_STEPS.general
  );

  const [nextStep, setNextStep] = useState<SmartConnectorStep>(
    SMART_CONNECTOR_STEPS.general
  );

  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [needsDeactivation, setNeedsDeactivation] = useState<boolean>(false);

  const [isValid, setIsValid] = useState<boolean>(true);

  const [refreshing, setRefreshing] = useState<boolean>(false);

  const [stepData, setStepData] = useState<Partial<SmartConnector>>({});

  const [errors, setErrors] = useState<SmartConnectorErrors>({});

  const [forceResetScript, setForceResetScript] = useState<number>(0);

  const [templateScript, setTemplateScript] = useState<string>('');

  const { deactivationModalProps, showDeactivationModalWithResult } =
    useDeactivationModal<SmartConnectorPatchResult>({ initial: initialResult });

  // refs //////////////////////////////////////////////////////////

  const isNew = useRef<boolean | undefined>();

  const smartConnectorRef = useRef(smartConnector);
  smartConnectorRef.current = smartConnector;

  const stepRef = useRef(step);
  stepRef.current = step;

  const stepDataRef = useRef(stepData);
  stepDataRef.current = stepData;

  // effects //////////////////////////////////////////////////////////

  useSmartConnectorTitle(smartConnector);

  const [{ loading }, fetchSmartConnector] = useAsyncFn(
    async () => {
      try {
        const { data } = await SmartConnectorService.getSmartConnector(id);
        isNew.current ??= !data.source_file?.id;
        setSmartConnector(data);
        setStepData(getStepData(data, stepRef.current));
        return data;
      } catch (err: any) {
        if (err.isAxiosError) {
          dispatch(setError({ code: err.response?.status }));
        }
      }
    },
    [id],
    { loading: true }
  );

  const {
    value: metaData = defaultContext.metaData,
    loading: loadingMetaData,
  } = useAsync<() => Promise<SmartConnectorMetaData>>(async () => {
    const { data } = await SmartConnectorService.getSmartConnectorMetaData();
    return data;
  });

  const { loading: loadingTemplateScript } = useAsync(async () => {
    if (
      smartConnector.source_file?.id &&
      step === SMART_CONNECTOR_STEPS.connection
    ) {
      const { data } = await SmartConnectorService.getScriptTemplate(
        smartConnector.id,
        smartConnector.source_file.id
      );
      setTemplateScript(data.user_script || '');
    }
  }, [smartConnector.source_file?.id, smartConnector.id, step]);

  useEffect(() => {
    fetchSmartConnector();
  }, [fetchSmartConnector]);

  useLayoutEffect(() => {
    setNextStep(step);

    //initiates stepData and resets errors for each step
    setStepData(getStepData(smartConnectorRef.current, step));
    setErrors({});
  }, [step]);

  useEffect(() => {
    const fields = STEP_DATA_BY_STEP[step];
    const isDirty = fields.some((field: keyof SmartConnectorBase) => {
      return !isEqual(smartConnector[field], stepData[field]);
    });

    const shouldDeactivate = PROPS_THAT_NEED_DEACTIVATION_ON_CHANGE.some(
      (field: keyof SmartConnectorBase) => {
        return (
          field in stepData && !isEqual(smartConnector[field], stepData[field])
        );
      }
    );
    setIsDirty(isDirty);
    setNeedsDeactivation(shouldDeactivate);
  }, [smartConnector, step, stepData]);

  // handlers //////////////////////////////////////////////////////////

  const handleGoToStep = useCallback(
    (step: SmartConnectorStep) => {
      if (isDirty) {
        setNextStep(step);
      } else {
        setStep(step);
      }
    },
    [isDirty]
  );

  const patchSmartConnector = useCallback(
    async (
      payload: SmartConnectorDTO,
      withClose: boolean,
      skipNavigation: boolean = false
    ): Promise<SmartConnectorPatchResult> => {
      try {
        setRefreshing(true);
        setErrors({});

        const { data } = await SmartConnectorService.updateSmartConnector(
          id,
          payload
        );

        const { last_draft_script } = stepDataRef.current;
        if (
          step === SMART_CONNECTOR_STEPS.connection &&
          (last_draft_script?.user_script !==
            smartConnector.last_draft_script?.user_script ||
            last_draft_script?.sql_version !==
              smartConnector.last_draft_script?.sql_version)
        ) {
          const { data: scriptData } =
            await SmartConnectorService.updateSmartConnectorScript(
              smartConnector.id,
              data.last_draft_script?.id, // last_draft_script can be recreated at this moment and can have new id
              {
                user_script: last_draft_script?.user_script || '',
                sql_version: last_draft_script?.sql_version,
              }
            );
          data.last_draft_script = {
            ...data.last_draft_script,
            ...scriptData,
          };
        }

        setSmartConnector(data);
        setStepData(getStepData(data, step));
        setIsDirty(false);

        if (isNew.current && !skipNavigation) {
          if (step === SMART_CONNECTOR_STEPS.general) {
            setStep(SMART_CONNECTOR_STEPS.connection);
          }
          if (step === SMART_CONNECTOR_STEPS.connection) {
            setStep(SMART_CONNECTOR_STEPS.variables);
          }
        }

        if (withClose) {
          setTimeout(() =>
            history.push(`${SMARTCONNECTORS_PATHNAME}/${id}/builder`)
          );
        }

        return [true, data];
      } catch (error) {
        if (error instanceof AxiosError) {
          const axiosError = (error as AxiosError<SmartConnectorErrors>)
            .response?.data;

          const errors: SmartConnectorErrors = {};
          if (axiosError?.name) {
            errors.name = axiosError.name;
          }
          if (axiosError?.api_name) {
            errors.api_name = axiosError.api_name;
          }
          if (axiosError?.custom_object) {
            errors.custom_object = axiosError.custom_object;
          }
          if (axiosError?.error_notification_email) {
            errors.error_notification_email =
              axiosError.error_notification_email;
          }
          if (axiosError?.all_notification_email) {
            errors.all_notification_email = axiosError.all_notification_email;
          }
          if (axiosError?.execution_variables) {
            errors.execution_variables = axiosError.execution_variables;
          }
          if (axiosError?.max_concurrent_executions) {
            errors.max_concurrent_executions =
              axiosError.max_concurrent_executions;
          }
          if (axiosError?.sql_version) {
            errors.sql_version = axiosError.sql_version;
          }
          if (axiosError?.cadence) {
            errors.cadence = axiosError.cadence;
          }
          if (axiosError?.description) {
            errors.description = axiosError.description;
          }
          if (axiosError?.schedule_rrule) {
            errors.schedule_rrule = axiosError.schedule_rrule;
          }
          if (axiosError?.bulkaction_fields) {
            errors.bulkaction_fields = axiosError.bulkaction_fields;
          }
          setErrors(errors);
        }
        return [false, null];
      } finally {
        setRefreshing(false);
      }
    },
    [id, step, smartConnector, history]
  );

  const handleSave = useCallback(
    (
      withClose: boolean,
      skipNavigation: boolean = false
    ): Promise<SmartConnectorPatchResult> => {
      if (
        isDirty &&
        needsDeactivation &&
        getIsSmartConnectorActive({ status: smartConnector.status })
      ) {
        return showDeactivationModalWithResult({
          handleSave: () => {
            return patchSmartConnector(
              {
                ...getSmartConnectorDTO(stepDataRef.current),
                status: SMART_CONNECTOR_STATUSES.inactive,
              },
              withClose,
              skipNavigation
            );
          },
          handleDiscard: () => {
            setSmartConnector(smartConnectorRef.current);
            setStepData(
              getStepData(smartConnectorRef.current, stepRef.current)
            );
            setForceResetScript((prev) => prev + 1);
            return [null, null];
          },
        });
      }
      return patchSmartConnector(
        getSmartConnectorDTO(stepDataRef.current),
        withClose,
        skipNavigation
      );
    },
    [
      patchSmartConnector,
      smartConnector.status,
      showDeactivationModalWithResult,
      isDirty,
      needsDeactivation,
    ]
  );

  // we have logic to update some smartConnector properties without clicking Save buttons
  // like clicking Test Execution button actually updates last_draft_script on BE,
  // so this is used to update needed parts of smartConnector and stepData in sync
  const handleUpdate = useCallback(
    async (promise: Promise<Partial<SmartConnector>>) => {
      setRefreshing(true);
      try {
        const data = await promise;
        flushSync(() =>
          setSmartConnector((prev) => {
            const nextData = { ...prev, ...data };

            setStepData((prev) => ({
              ...prev,
              ...getStepData(nextData, stepRef.current),
            }));

            return nextData;
          })
        );

        return true;
      } catch (err) {
        monitoringExceptionHelper(err);
        return false;
      } finally {
        setRefreshing(false);
      }
    },
    []
  );

  return {
    smartConnector,
    metaData,
    loading:
      loading || loadingMetaData || (!templateScript && loadingTemplateScript),
    refreshing,
    handleGoToStep,
    step,
    setStep,
    nextStep,
    setNextStep,
    isNew: !!isNew.current,
    isDirty,
    setIsDirty,
    handleSave,
    stepData,
    setStepData,
    errors,
    setErrors,
    handleUpdate,
    refetchSmartConnector: fetchSmartConnector,
    isValid,
    setIsValid,
    deactivationModalProps,
    showDeactivationModalWithResult,
    forceResetScript,
    setForceResetScript,
    templateScript,
    setTemplateScript,
  };
};
